import React, { useState, useEffect } from 'react';
import { Plus, Coins, RefreshCw, ExternalLink } from 'lucide-react';
import { useWallet } from '@solana/wallet-adapter-react';
import { ApiService, Token } from '../../services/api';
import { SignedTransactionService } from '../../services/signedTransactionService';
import toast from 'react-hot-toast';
import MintTokenModal from '../../components/modals/admin/MintTokenModal';

const AdminTokenFactory: React.FC = () => {
  const { connected, publicKey, signTransaction } = useWallet();
  
  const [tokens, setTokens] = useState<Token[]>([]);
  const [loading, setLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [showMintModal, setShowMintModal] = useState(false);
  const [selectedToken, setSelectedToken] = useState<Token | null>(null);

  // Form state
  const [name, setName] = useState('');
  const [symbol, setSymbol] = useState('');
  const [logoUrl, setLogoUrl] = useState('');
  const [initialSupply, setInitialSupply] = useState('');
  const [decimals, setDecimals] = useState('9');
  const [valueType, setValueType] = useState('');

  // Value type options
  const valueTypeOptions = [
    { value: '', label: 'Select Value Type (Optional)' },
    { value: 'Gift cards', label: 'Gift cards' },
    { value: 'Loyalty & reward points', label: 'Loyalty & reward points' },
    { value: 'Airline or travel miles', label: 'Airline or travel miles' },
    { value: 'Store credits / refunds', label: 'Store credits / refunds' },
    { value: 'Prepaid balances', label: 'Prepaid balances' },
    { value: 'Partner rebates or B2B credits', label: 'Partner rebates or B2B credits' }
  ];

  useEffect(() => {
    fetchTokens();
  }, []);

  const fetchTokens = async () => {
    try {
      setLoading(true);
      const data = await ApiService.getTokens();
      setTokens(data);
    } catch (error) {
      console.error('Failed to fetch tokens:', error);
      toast.error('Failed to load tokens');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateToken = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!connected || !publicKey || !signTransaction) {
      toast.error('Please connect your wallet first');
      return;
    }

    if (!name || !symbol || !initialSupply) {
      toast.error('Please fill in all required fields');
      return;
    }

    const supply = parseFloat(initialSupply);
    if (supply < 0) {
      toast.error('Initial supply must be non-negative');
      return;
    }

    setIsCreating(true);

    try {
      const wallet = {
        publicKey,
        signTransaction,
      };

      // Build the transaction
      const buildResponse = await ApiService.buildCreateTokenTransaction({
        name,
        symbol: symbol.toUpperCase(),
        logoUrl: logoUrl || undefined,
        initialSupply: supply,
        decimals: parseInt(decimals),
        valueType: valueType || undefined,
        walletAddress: publicKey.toString(),
      });

      // Sign and send transaction
      const result = await SignedTransactionService.signAndSendTransaction(
        buildResponse.transaction,
        wallet
      );

      if (result.success && result.signature) {
        // Save the result to backend
        const token = await ApiService.saveTokenCreationResult({
          name,
          symbol: symbol.toUpperCase(),
          logoUrl: logoUrl || undefined,
          initialSupply: supply,
          decimals: parseInt(decimals),
          valueType: valueType || undefined,
          mintAddress: buildResponse.mintAddress,
          mintAuthority: publicKey.toString(),
          solanaSignature: result.signature,
        });

        toast.success(`Token ${token.symbol} created successfully!`);
        
        // Reset form
        setName('');
        setSymbol('');
        setLogoUrl('');
        setInitialSupply('');
        setDecimals('9');
        setValueType('');
        setShowCreateForm(false);
        
        // Refresh tokens list
        fetchTokens();
      } else {
        throw new Error(result.error || 'Transaction failed');
      }
    } catch (error: any) {
      console.error('Failed to create token:', error);
      toast.error(error.response?.data?.message || error.message || 'Failed to create token');
    } finally {
      setIsCreating(false);
    }
  };

  const handleMintMore = (token: Token) => {
    setSelectedToken(token);
    setShowMintModal(true);
  };

  const handleSyncSupply = async (tokenId: string) => {
    try {
      await ApiService.syncTokenSupply(tokenId);
      toast.success('Token supply synced successfully');
      fetchTokens();
    } catch (error: any) {
      console.error('Failed to sync supply:', error);
      toast.error(error.response?.data?.message || 'Failed to sync supply');
    }
  };

  const formatSupply = (supply: number, decimals: number) => {
    return (supply / Math.pow(10, decimals)).toLocaleString(undefined, {
      maximumFractionDigits: decimals,
    });
  };

  const handleViewOnSolscan = (tokenAddress: string) => {
    const network = import.meta.env.VITE_SOLANA_NETWORK || 'devnet';
    const url = `https://solscan.io/token/${tokenAddress}${network === 'devnet' ? '?cluster=devnet' : ''}`;
    window.open(url, '_blank');
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-white">Token Factory</h1>
          <p className="text-sm sm:text-base text-gray-400">Create and manage SPL tokens on Solana</p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2 sm:space-x-3 sm:space-y-0 w-full sm:w-auto">
          <button
            onClick={fetchTokens}
            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-gray-700 text-sm font-medium rounded-md text-white bg-[#182233] hover:bg-[#1F2937] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2777EF]"
          >
            <RefreshCw className="mr-2 h-4 w-4" />
            Refresh
          </button>
          <button
            onClick={() => setShowCreateForm(!showCreateForm)}
            className="w-full sm:w-auto inline-flex items-center justify-center sm:justify-start px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-[#2777EF] hover:bg-[#1F5FD3] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#2777EF]"
          >
            <Plus className="mr-2 h-4 w-4" />
            {showCreateForm ? 'Hide Form' : 'Create Token'}
          </button>
        </div>
      </div>

      {/* Create Token Form */}
      {showCreateForm && (
        <div className="bg-[#182233] shadow rounded-lg p-6">
          <h3 className="text-lg font-medium text-white mb-4">Create New Token</h3>
          <form onSubmit={handleCreateToken} className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Token Name *
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="e.g., Loop Token"
                  className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                  required
                  disabled={isCreating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Symbol *
                </label>
                <input
                  type="text"
                  value={symbol}
                  onChange={(e) => setSymbol(e.target.value.toUpperCase())}
                  placeholder="e.g., LOOP"
                  maxLength={10}
                  className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                  required
                  disabled={isCreating}
                />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Initial Supply *
                </label>
                <input
                  type="number"
                  value={initialSupply}
                  onChange={(e) => setInitialSupply(e.target.value)}
                  placeholder="e.g., 1000000"
                  min="0"
                  step="any"
                  className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                  required
                  disabled={isCreating}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  Decimals
                </label>
                <input
                  type="number"
                  value={decimals}
                  onChange={(e) => setDecimals(e.target.value)}
                  placeholder="9"
                  min="0"
                  max="18"
                  className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                  disabled={isCreating}
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Value Type (optional)
              </label>
              <select
                value={valueType}
                onChange={(e) => setValueType(e.target.value)}
                className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                disabled={isCreating}
              >
                {valueTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Logo URL (optional)
              </label>
              <input
                type="url"
                value={logoUrl}
                onChange={(e) => setLogoUrl(e.target.value)}
                placeholder="https://example.com/logo.png"
                className="w-full px-4 py-2 bg-[#0D1421] border border-gray-700 rounded-lg text-white focus:outline-none focus:border-[#2777EF]"
                disabled={isCreating}
              />
            </div>

            <div className="flex flex-col sm:flex-row sm:justify-end gap-2 sm:gap-3 sm:space-x-0">
              <button
                type="button"
                onClick={() => setShowCreateForm(false)}
                disabled={isCreating}
                className="w-full sm:w-auto px-4 py-2 bg-gray-700 text-white rounded-lg hover:bg-gray-600 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isCreating}
                className="w-full sm:w-auto px-4 py-2 bg-[#2777EF] text-white rounded-lg hover:bg-[#1F5FD3] transition-colors disabled:opacity-50 flex items-center justify-center sm:justify-start"
              >
                {isCreating ? (
                  <>
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                    Creating Token...
                  </>
                ) : (
                  <>
                    <Coins className="mr-2 h-4 w-4" />
                    Create Token
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Tokens List */}
      <div className="bg-[#182233] shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <h3 className="text-lg leading-6 font-medium text-white mb-4">Created Tokens</h3>
          
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2777EF]"></div>
            </div>
          ) : tokens.length === 0 ? (
            <div className="text-center py-8">
              <Coins className="mx-auto h-12 w-12 text-gray-600" />
              <p className="mt-2 text-sm text-gray-400">No tokens created yet</p>
              <p className="text-xs text-gray-500 mt-1">Click "Create Token" to get started</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-700">
                <thead>
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Token
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Token Address
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Supply
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Decimals
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Value Type
                    </th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Created
                    </th>
                    <th className="px-6 py-3 text-right text-xs font-medium text-gray-400 uppercase tracking-wider">
                      Actions
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-700">
                  {tokens.map((token) => (
                    <tr key={token.id} className="hover:bg-[#1F2937]">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          {token.logoUrl && (
                            <img
                              src={token.logoUrl}
                              alt={token.symbol}
                              className="h-8 w-8 rounded-full mr-3"
                              onError={(e) => {
                                e.currentTarget.style.display = 'none';
                              }}
                            />
                          )}
                          <div>
                            <div className="text-sm font-medium text-white">{token.name}</div>
                            <div className="text-sm text-gray-400">{token.symbol}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="flex items-center">
                          <span className="text-sm text-gray-300 font-mono">
                            {token.tokenAddress.slice(0, 4)}...{token.tokenAddress.slice(-4)}
                          </span>
                          <button
                            onClick={() => handleViewOnSolscan(token.tokenAddress)}
                            className="ml-2 text-[#2777EF] hover:text-[#1F5FD3]"
                          >
                            <ExternalLink className="h-4 w-4" />
                          </button>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm text-white">
                          {formatSupply(token.currentSupply, token.decimals)}
                        </div>
                        {token.currentSupply !== token.initialSupply && (
                          <div className="text-xs text-gray-500">
                            Initial: {formatSupply(token.initialSupply, token.decimals)}
                          </div>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {token.decimals}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-300">
                        {token.valueType || (
                          <span className="text-gray-500 italic">Not specified</span>
                        )}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-400">
                        {new Date(token.createdAt).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                        <button
                          onClick={() => handleSyncSupply(token.id)}
                          className="text-gray-400 hover:text-white mr-3"
                          title="Sync supply"
                        >
                          <RefreshCw className="h-4 w-4" />
                        </button>
                        <button
                          onClick={() => handleMintMore(token)}
                          className="text-[#2777EF] hover:text-[#1F5FD3]"
                        >
                          Mint More
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* Mint Token Modal */}
      <MintTokenModal
        isOpen={showMintModal}
        onClose={() => {
          setShowMintModal(false);
          setSelectedToken(null);
        }}
        token={selectedToken}
        onSuccess={fetchTokens}
      />
    </div>
  );
};

export default AdminTokenFactory;

